import classNames from 'classnames';
import LazyLoad from 'react-lazyload';
import Button from '@alltrails/shared/denali/components/Button';
import Texture from '@alltrails/shared/denali/components/Texture';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import { LanguageSupportUtil } from 'utils/language_support_util';
import { FormattedMessage, useIntl } from '@alltrails/shared/react-intl';
import useUser from 'hooks/useUser';
import useMobileWidth from 'hooks/useMobileWidth';
import { MEDIA_QUERY_MOBILE_DOWN } from 'utils/constants/breakpoints';
import logHomepageProUpsellBlockButtonClicked from '@alltrails/analytics/events/logHomepageProUpsellBlockButtonClicked';
import FeatureRow from './FeatureRow';
import * as styles from './PlusUpsell.module.scss';
import logoPlus from '../PlusUpsellNoAuth/logo_plus.png';
import mobileSrc from './plusUpsellAuth-w686.jpg';
import mobileSrcAvif from './plusUpsellAuth-w686.avif';
import mobileSrcWebp from './plusUpsellAuth-w686.webp';
import webSrc from './plusUpsellAuth-w1234.jpg';
import webSrcAvif from './plusUpsellAuth-w1234.avif';
import webSrcWebp from './plusUpsellAuth-w1234.webp';

export default function PlusUpsell() {
  const languageRegionCode = useLanguageRegionCode();
  const user = useUser();
  const intl = useIntl();
  const imageAlt = intl.formatMessage({
    defaultMessage: 'Three images depicting a person walking up stairs, swimming, and mountain biking'
  });
  const isMobileView = useMobileWidth();

  const texture = <Texture variant="plus" className={styles.texture} imageSize="cropped" />;

  return (
    <div className={styles.container}>
      <div className={styles.tableAndImageContainer}>
        <div className={styles.upsellTable}>
          <LazyLoad>
            <img className={styles.logoPlusImage} src={logoPlus} alt="all-trails-plus" />
          </LazyLoad>
          <h2 className={styles.title}>
            <FormattedMessage defaultMessage="More tools for more adventures" />
          </h2>
          <div className={styles.headerRow}>
            <div className={classNames(styles.tableHeader, styles.headerRowFeature)}>
              <FormattedMessage defaultMessage="Features" />
            </div>
            <div className={classNames(styles.tableHeader, styles.headerRowFreePro)}>
              <FormattedMessage defaultMessage="Free" />
            </div>
            <div className={classNames(styles.tableHeader, styles.headerRowFreePro)}>AllTrails+</div>
          </div>
          <FeatureRow free tint text={<FormattedMessage defaultMessage="Navigate on the trail" />} />
          <FeatureRow free tint={false} text={<FormattedMessage defaultMessage="Favorite trails and create lists" />} />
          <FeatureRow free={false} tint text={<FormattedMessage defaultMessage="Download offline maps" />} />
          <FeatureRow free={false} tint={false} text={<FormattedMessage defaultMessage="Get alerts for wrong turns" />} />
          <FeatureRow free={false} tint text={<FormattedMessage defaultMessage="Find trails by distance from you" />} />
          <FeatureRow free={false} tint={false} text={<FormattedMessage defaultMessage="Unlock more map details" />} />
          <div className={styles.buyLink}>
            <Button
              text={
                !user || user.isPromoEligible ? (
                  <FormattedMessage defaultMessage="Try AllTrails+ free" />
                ) : (
                  <FormattedMessage defaultMessage="Buy AllTrails+" />
                )
              }
              linkInfo={{ href: LanguageSupportUtil.wrapUrlSafe('/plus?ref=homepage_pro_proupsell', languageRegionCode) }}
              onClick={() => logHomepageProUpsellBlockButtonClicked()}
              testId="plus-upsell-try-or-buy-plus"
              variant="accent"
            />
          </div>
        </div>
        {isMobileView && texture}
        <div className={styles.imageContainer}>
          <picture>
            <source media={MEDIA_QUERY_MOBILE_DOWN} srcSet={mobileSrc} type="image/jpg" />
            <source media={MEDIA_QUERY_MOBILE_DOWN} srcSet={mobileSrcAvif} type="image/avif" />
            <source media={MEDIA_QUERY_MOBILE_DOWN} srcSet={mobileSrcWebp} type="image/webp" />
            <source srcSet={webSrcAvif} type="image/avif" />
            <source srcSet={webSrcWebp} type="image/webp" />
            <img src={webSrc} alt={imageAlt} className={styles.image} loading="lazy" />
          </picture>
        </div>
      </div>
      {!isMobileView && texture}
    </div>
  );
}
